import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Seo from '../components/Seo';
import GyanSparkLogoDark from '../media/gyanspark-logo-dark-horizontal.svg';
import heroImg from '../media/generated/gyanspark-hero.webp';
import overTimeImg from '../media/generated/gyanspark-over-time.webp';
import integrationImg from '../media/generated/gyanspark-integration.webp';
import { Section, SectionHeading, PageHero, Card, IconBadge, Steps, Faq, CtaBand, fadeUp } from '../components/ui';
import { ArrowUpRightIcon, CheckIcon, PuzzleIcon, BrainIcon, ChatIcon, BookIcon, LinkNodeIcon, SparkleIcon } from '../components/icons';

const SITE = 'https://www.gyanspark.com/';

const WhatItDoes = () => (
  <Section>
    <div className="max-w-3xl mx-auto text-center">
      <motion.div {...fadeUp}>
        <span className="text-sm font-bold tracking-widest uppercase text-amber-400">What it actually does</span>
        <h2 className="text-2xl md:text-4xl font-extrabold mt-3 mb-6 text-white leading-snug">
          “The answer was already correct. Then the tool learned who was asking.”
        </h2>
        <p className="text-lg text-gray-400 leading-relaxed">
          Your AI features already return good answers. What they don't do is remember the student. GyanSpark
          adds that memory — so a correct answer becomes a correct answer <em>for this learner</em>, shaped by
          everything they've struggled with before.
        </p>
      </motion.div>
    </div>
  </Section>
);

const Node = ({ title, items, tone = 'gray' }) => {
  const tones = {
    gray: 'bg-white/[0.04] border-white/10',
    amber: 'bg-amber-500/10 border-amber-400/40 ring-2 ring-amber-400/20',
    purple: 'bg-purple-500/10 border-purple-400/30',
  };
  return (
    <div className={`rounded-2xl border p-5 text-center flex-1 ${tones[tone]}`}>
      <h3 className="font-bold text-white text-sm mb-3">{title}</h3>
      <ul className="space-y-1.5">
        {items.map((i) => (
          <li key={i} className="text-xs text-gray-300 bg-white/5 rounded-lg py-1.5 px-2 border border-white/10">
            {i}
          </li>
        ))}
      </ul>
    </div>
  );
};

const Connector = () => (
  <div className="flex md:flex-col items-center justify-center text-amber-400 py-2 md:py-0 md:px-1">
    <span className="hidden md:block text-2xl leading-none">→</span>
    <span className="md:hidden text-2xl leading-none">↓</span>
  </div>
);

const Architecture = () => (
  <Section tone="raised">
    <SectionHeading
      accent="amber"
      eyebrow="How it fits"
      title="It sits between your tools and your students."
      subtitle="No new hosting infrastructure, no rebuild. GyanSpark wraps the AI features you already run."
    />
    <motion.div {...fadeUp} className="max-w-4xl mx-auto">
      <div className="flex flex-col md:flex-row items-stretch gap-2">
        <Node title="Your AI tools" items={['Doubt solver', 'Practice sets', 'Quiz generation', 'Notes generation']} />
        <Connector />
        <Node title="GyanSpark" items={['One learner profile', 'Cross-tool memory', 'Gap detection', 'Adaptive intervention']} tone="amber" />
        <Connector />
        <Node title="Your students" items={['Consistent support', 'Fewer repeat gaps', 'Personalized help', 'Better outcomes']} tone="purple" />
      </div>
      <p className="text-center text-sm text-gray-500 mt-6">
        Every tool writes to and reads from the same learner profile — so personalization compounds instead of resetting.
      </p>
    </motion.div>
  </Section>
);

const SupportedTools = () => (
  <Section>
    <SectionHeading accent="amber" eyebrow="Supported tools" title="Works with the features you've already built" />
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
      {[
        { icon: ChatIcon, label: 'Doubt solver' },
        { icon: PuzzleIcon, label: 'Practice sets' },
        { icon: CheckIcon, label: 'Quiz generation' },
        { icon: BookIcon, label: 'Notes generation' },
      ].map((t, i) => (
        <motion.div key={t.label} {...fadeUp} transition={{ duration: 0.4, delay: i * 0.08 }} className="p-6 rounded-2xl bg-white/[0.04] border border-white/10 text-center">
          <IconBadge icon={t.icon} tone="amber" className="mx-auto mb-3" />
          <div className="text-sm font-semibold text-white">{t.label}</div>
        </motion.div>
      ))}
    </div>
    <div className="flex flex-wrap items-center justify-center gap-3 mt-10">
      <span className="text-sm font-semibold text-gray-500">Compatible with:</span>
      {['OpenAI', 'Claude', 'Gemini'].map((p) => (
        <span key={p} className="px-4 py-1.5 rounded-full text-sm font-semibold bg-amber-500/10 text-amber-300 border border-amber-400/20">
          {p}
        </span>
      ))}
    </div>
  </Section>
);

const TwoWaysIn = () => (
  <Section tone="raised">
    <SectionHeading
      accent="amber"
      eyebrow="Two ways in"
      title="However far along you are, there's a path"
      subtitle="Whether you already run AI features or you're starting from your content, integration looks different — and both are supported."
    />
    <motion.div {...fadeUp} className="mb-10">
      <img src={integrationImg} alt="Illustration of two modules joined by a connector" className="w-full max-w-xs mx-auto" />
    </motion.div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
      {[
        {
          icon: LinkNodeIcon,
          label: 'Already have AI tools',
          headline: 'Wrap what you already run.',
          desc: 'Keep your existing doubt solver, quiz generator, or practice engine exactly as it is. GyanSpark layers learner context around it.',
        },
        {
          icon: SparkleIcon,
          label: 'No AI yet',
          headline: 'Start with your own content.',
          desc: "If you haven't built AI features yet, GyanSpark can work from your existing content library as the foundation for personalization.",
        },
      ].map((opt) => (
        <Card key={opt.label} hover>
          <IconBadge icon={opt.icon} tone="amber" className="mb-4" />
          <span className="text-xs font-bold tracking-widest uppercase text-amber-400">{opt.label}</span>
          <h3 className="font-extrabold text-white mt-2 mb-2">{opt.headline}</h3>
          <p className="text-sm text-gray-400 leading-relaxed">{opt.desc}</p>
        </Card>
      ))}
    </div>
  </Section>
);

const OverTime = () => (
  <Section>
    <div className="grid lg:grid-cols-2 gap-12 items-center max-w-5xl mx-auto">
      <motion.div {...fadeUp} className="order-2 lg:order-1">
        <img src={overTimeImg} alt="Illustration of panels becoming sharper from left to right" className="w-full max-w-lg mx-auto" />
      </motion.div>
      <motion.div {...fadeUp} transition={{ duration: 0.6, delay: 0.1 }} className="order-1 lg:order-2">
        <span className="text-sm font-bold tracking-widest uppercase text-amber-400">Learning over time</span>
        <h2 className="text-2xl md:text-4xl font-extrabold mt-3 mb-5 text-white">
          It gets sharper the longer a student stays.
        </h2>
        <p className="text-lg text-gray-400 leading-relaxed mb-6">
          One wrong answer is noise. The same wrong idea, four times, across three tools, is a pattern worth
          acting on. GyanSpark tells the difference — and adjusts when and how your platform intervenes.
        </p>
        <div className="flex items-start gap-3 p-5 rounded-2xl bg-amber-500/10 border border-amber-400/20">
          <BrainIcon className="w-6 h-6 text-amber-400 flex-shrink-0 mt-0.5" />
          <p className="text-gray-200 italic">
            “The same mistake in four sessions, across three different tools. Now it's worth naming.”
          </p>
        </div>
      </motion.div>
    </div>
  </Section>
);

const Integration = () => (
  <Section tone="raised">
    <SectionHeading accent="amber" eyebrow="Getting started" title="Integration in three steps" />
    <div className="max-w-4xl mx-auto">
      <Steps
        tone="amber"
        steps={[
          { title: 'Book a demo', desc: 'Walk through your current stack with the team and see where GyanSpark fits.' },
          { title: 'Connect your tools', desc: 'Point your existing AI features at GyanSpark — or start from your content if you have no AI yet.' },
          { title: 'Personalization compounds', desc: 'Every session adds to the learner profile, and every feature you run gets the benefit.' },
        ]}
      />
    </div>
  </Section>
);

const GyansparkFaq = () => (
  <Section>
    <SectionHeading accent="amber" eyebrow="FAQ" title="Questions about GyanSpark" />
    <Faq
      items={[
        {
          q: 'What exactly is GyanSpark?',
          a: 'GyanSpark is a personalization layer for education platforms. It sits as middleware between the AI tools a platform has already built and the students using them, maintaining one shared learner profile that every feature can read from and write to.',
        },
        {
          q: 'Do I need to change my hosting or rebuild my platform?',
          a: 'No. GyanSpark is designed to be non-intrusive — it works around your existing tools without requiring new hosting infrastructure.',
        },
        {
          q: 'Which AI providers does it work with?',
          a: 'GyanSpark works with OpenAI, Claude, and Gemini, so it fits whichever provider your platform already uses.',
        },
        {
          q: 'What if my platform has no AI features yet?',
          a: 'That is the second supported path. Instead of wrapping existing AI tools, GyanSpark can start from your own content library as the basis for personalization.',
        },
        {
          q: 'Which kinds of tools can it personalize?',
          a: 'Doubt solvers, practice sets, quiz generation, and notes generation are all supported. Because the learner profile is shared, the personalization carries consistently across all of them.',
        },
        {
          q: 'How does it decide when to intervene?',
          a: 'GyanSpark distinguishes isolated mistakes from persistent misconceptions by tracking recurring gaps across sessions and across tools, then adjusts the timing and method of support accordingly.',
        },
      ]}
    />
  </Section>
);

const ProductGyanspark = () => (
  <>
    <Seo
      title="GyanSpark — Personalization Layer for EdTech Platforms"
      description="GyanSpark gives every feature you've built one shared understanding of each learner. Non-intrusive personalization middleware for EdTech platforms, compatible with OpenAI, Claude, and Gemini."
      path="/products/gyanspark"
    />
    <PageHero
      eyebrow="GyanSpark"
      title="Your platform should know every student."
      subtitle="GyanSpark gives every feature you've built one shared understanding of each learner."
      image={heroImg}
      imageAlt="Illustration of a personalization layer connecting multiple tools"
    >
      <div className="flex flex-col sm:flex-row gap-4">
        <a
          href={SITE}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full bg-white text-[#1a0033] font-semibold hover:bg-amber-500/10 transition-colors"
        >
          Book a demo <ArrowUpRightIcon className="w-4 h-4" />
        </a>
        <Link
          to="/products"
          className="px-8 py-3.5 rounded-full border border-white/30 text-white font-semibold hover:bg-white/10 transition-colors text-center"
        >
          Compare products
        </Link>
      </div>
      <img src={GyanSparkLogoDark} alt="GyanSpark logo" className="h-8 object-contain mt-10" />
    </PageHero>
    <WhatItDoes />
    <Architecture />
    <SupportedTools />
    <TwoWaysIn />
    <OverTime />
    <Integration />
    <GyansparkFaq />
    <CtaBand title="Ready to personalize your platform?" subtitle="One shared understanding. Every AI tool, personal.">
      <a
        href={SITE}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-white text-[#1a0033] font-semibold hover:bg-purple-500/10 transition-colors"
      >
        Book a demo <ArrowUpRightIcon className="w-4 h-4" />
      </a>
    </CtaBand>
  </>
);

export default ProductGyanspark;
